<template>
  <!-- ═══════════ Mobile: Account screen ═══════════ -->
  <div v-if="isMobile" class="acct">
    <header class="acct-head"><h1 class="acct-title">Account</h1></header>

    <div class="acct-card acct-user">
      <span class="acct-avatar">{{ initials }}</span>
      <span class="acct-user-text">
        <span class="acct-name">{{ displayName }}</span>
        <span class="acct-sub">{{ userSubline }}</span>
      </span>
    </div>

    <div v-if="hasSubscription && plan" class="acct-card acct-plan">
      <div class="acct-plan-head">
        <span class="acct-plan-name">{{ plan.name }}</span>
        <span class="acct-plan-status">Active</span>
      </div>
      <div class="acct-plan-bar"><div class="acct-plan-fill" :style="{ width: usagePct + '%' }"></div></div>
      <span class="acct-plan-meta">{{ num(plan.usedMinutes) }} / {{ num(plan.monthlyMinutes) }} video minutes<span v-if="renewalLabel"> · renews {{ renewalLabel }}</span></span>
    </div>

    <section class="acct-models">
      <LocalModelCacheTable />
    </section>

    <div class="acct-card acct-menu">
      <button type="button" class="acct-row" @click="openHelp">
        <v-icon size="19" color="#8aa0aa">mdi-help-circle-outline</v-icon>
        <span class="acct-row-label">Help &amp; docs</span>
        <v-icon size="18" color="#5c6f78">mdi-chevron-right</v-icon>
      </button>
      <button v-if="canOrgAdmin" type="button" class="acct-row" @click="go('Admin')">
        <v-icon size="19" color="#8aa0aa">mdi-cog-outline</v-icon>
        <span class="acct-row-label">Admin</span>
        <v-icon size="18" color="#5c6f78">mdi-chevron-right</v-icon>
      </button>
      <button v-if="canDualloAdmin" type="button" class="acct-row" @click="go('DualloAdmin')">
        <v-icon size="19" color="#8aa0aa">mdi-shield-crown-outline</v-icon>
        <span class="acct-row-label">Duallo admin</span>
        <v-icon size="18" color="#5c6f78">mdi-chevron-right</v-icon>
      </button>
      <button type="button" class="acct-row danger" @click="requestLogout">
        <v-icon size="19" color="#ff8a80">mdi-logout</v-icon>
        <span class="acct-row-label">Sign out</span>
      </button>
    </div>

    <div class="acct-note">
      <v-icon size="18" color="#8aa0aa">mdi-monitor</v-icon>
      <span>Asset settings, model versions, scans and adding markers are on the desktop app.</span>
    </div>
  </div>

  <!-- ═══════════ Desktop: Account (tabbed) ═══════════ -->
  <div v-else class="profile">
    <header class="profile-head">
      <h1 class="profile-title">Account</h1>
      <p class="profile-sub">Your account details and locally cached models.</p>
    </header>

    <div class="tabbar" role="tablist">
      <button type="button" class="tab" :class="{ active: tab === 'profile' }" role="tab"
        @click="tab = 'profile'">Account</button>
      <button type="button" class="tab" :class="{ active: tab === 'models' }" role="tab"
        @click="tab = 'models'">Models</button>
    </div>

    <div class="tab-body">
      <div v-if="tab === 'profile'" class="profile-grid">
        <BasicUserInformationCard :user="user" />

        <div v-if="hasSubscription && plan" class="acct-card acct-plan">
          <div class="acct-plan-head">
            <span class="acct-plan-name">{{ plan.name }}</span>
            <span class="acct-plan-status">Active</span>
          </div>
          <div class="acct-plan-bar"><div class="acct-plan-fill" :style="{ width: usagePct + '%' }"></div></div>
          <span class="acct-plan-meta">{{ num(plan.usedMinutes) }} / {{ num(plan.monthlyMinutes) }} video minutes<span v-if="renewalLabel"> · renews {{ renewalLabel }}</span></span>
        </div>

        <section class="set-card">
          <header class="set-head">
            <h3 class="set-title">Account settings</h3>
            <p class="set-sub">Manage your personal account.</p>
          </header>
          <p class="set-note">Personal account options will appear here.</p>
        </section>
      </div>

      <LocalModelCacheTable v-else-if="tab === 'models'" />
    </div>
  </div>
</template>

<script>
import { ref, computed } from 'vue';
import { useDisplay } from 'vuetify';
import { useAuth0 } from '@auth0/auth0-vue';
import { useRouter } from 'vue-router';
import BasicUserInformationCard from '@/components/cards/BasicUserInformationCard.vue';
import LocalModelCacheTable from '@/components/tables/LocalModelCacheTable.vue';
import OrganizationService from '@/services/OrganizationService';
import { useOrganizationStore } from '@/store/organizationStore';
import { useAssetStore } from '@/store/assetStore';
import { useAuthStore } from '@/store/authStore';
import { Permissions } from '@/constants/permissions';
import { usePermissions } from '@/composable/usePermissions';
import { useConfirm } from '@/composable/useConfirm';

export default {
  name: 'ProfileView',
  components: {
    BasicUserInformationCard,
    LocalModelCacheTable,
  },
  setup() {
    const { smAndDown } = useDisplay();
    const auth0 = useAuth0();
    const router = useRouter();
    const { can } = usePermissions();
    const organizationStore = useOrganizationStore();
    const assetStore = useAssetStore();
    const authStore = useAuthStore();

    const displayName = computed(() => auth0.user.value?.name || auth0.user.value?.nickname || 'Account');
    const initials = computed(() => {
      const n = (auth0.user.value?.name || auth0.user.value?.email || '?').trim();
      const parts = n.split(/[\s@.]+/).filter(Boolean);
      return ((parts[0]?.[0] || '?') + (parts[1]?.[0] || '')).toUpperCase();
    });
    const roleLabel = computed(() => {
      if (can(Permissions.DualloAdminView)) return 'Duallo admin';
      if (can(Permissions.OrganizationAdminView)) return 'Administrator';
      return 'Member';
    });
    const userSubline = computed(() => [auth0.user.value?.email, roleLabel.value].filter(Boolean).join(' · '));
    const canOrgAdmin = computed(() => can(Permissions.OrganizationAdminView));
    const canDualloAdmin = computed(() => can(Permissions.DualloAdminView));

    // Subscription (plan card)
    const hasSubscription = ref(!!organizationStore.active);
    const plan = ref(null);
    const usagePct = ref(0);
    const renewalLabel = ref(null);

    const loadSubscription = async () => {
      if (!hasSubscription.value) return;
      try {
        plan.value = await OrganizationService.getCurrentSubscriptionByOrganizationIdAsync(organizationStore.organizationId);
        if (plan.value?.monthlyMinutes) {
          usagePct.value = Math.min(Math.round((plan.value.usedMinutes / plan.value.monthlyMinutes) * 100), 100);
        }
        renewalLabel.value = formatRenewal(plan.value?.subscriptionPeriod);
      } catch { /* card just hides */ }
    };
    const formatRenewal = (period) => {
      if (!period) return null;
      const [year, month] = period.split('-').map(Number);
      if (!year || !month) return null;
      return new Date(Date.UTC(year, month, 1)).toLocaleDateString(undefined, { year: 'numeric', month: 'short', timeZone: 'UTC' });
    };
    const num = (n) => (n == null ? '0' : Number(n).toLocaleString());

    const go = (name) => { if (router.currentRoute.value.name !== name) router.push({ name }); };

    const openHelp = () => {
      const userName = auth0.user.value?.name || 'Unknown';
      const diagnostics = [
        `Name: ${userName}`,
        `User ID: ${auth0.user.value?.sub || 'Unknown'}`,
        `Organization ID: ${organizationStore.organizationId || 'Unknown'}`,
        `Page: ${window.location.href}`,
        `Timestamp: ${new Date().toLocaleString()}`,
        `Browser: ${navigator.userAgent}`,
      ].join('\n');
      const subject = encodeURIComponent(`Support Request — ${userName}`);
      const body = encodeURIComponent(
        `Hi Duallo Support,\n\nPlease describe your issue below:\n\n\n\n` +
        `────────────────────────\nDiagnostic details (please keep)\n────────────────────────\n${diagnostics}\n`);
      window.location.href = `mailto:support@duallo.ai?subject=${subject}&body=${body}`;
    };

    const { confirm } = useConfirm();
    const requestLogout = async () => {
      const ok = await confirm({
        title: 'Sign out?',
        message: "You'll need to sign in again to access your assets.",
        confirmText: 'Sign out',
        cancelText: 'Cancel',
        danger: true,
        icon: 'mdi-logout',
      });
      if (!ok) return;
      organizationStore.reset();
      assetStore.reset();
      authStore.reset();
      try { sessionStorage.clear(); } catch { /* best-effort */ }
      auth0.logout({ logoutParams: { returnTo: window.location.origin }, federated: true });
    };

    // Subscription/usage data feeds the plan card on both form factors, so load it unconditionally.
    loadSubscription();

    return {
      isMobile: smAndDown,
      user: auth0.user,
      displayName, initials, userSubline, canOrgAdmin, canDualloAdmin,
      hasSubscription, plan, usagePct, renewalLabel, num,
      go, openHelp, requestLogout,
    };
  },
  data() {
    return { tab: 'profile' };
  },
};
</script>

<style scoped>
/* ═══════════ Mobile Account ═══════════ */
.acct {
  display: flex; flex-direction: column; gap: 14px;
  color: #eef3f2;
}
.acct-head { margin-bottom: 2px; }
.acct-title { font-size: 1.6rem; font-weight: 700; color: #fff; margin: 0; }

.acct-card {
  background: var(--duallo-card-gradient);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 16px;
}
.acct-user { display: flex; align-items: center; gap: 12px; padding: 14px; }
.acct-avatar {
  flex: 0 0 auto; width: 48px; height: 48px; border-radius: 50%;
  background: linear-gradient(135deg, #a855f7 0%, #7c3aed 100%);
  display: flex; align-items: center; justify-content: center; color: #fff; font-size: 1rem; font-weight: 700;
}
.acct-user-text { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
.acct-name { font-size: 1rem; font-weight: 700; color: #fff; }
.acct-sub { font-size: 0.82rem; color: #8aa0aa; overflow: hidden; text-overflow: ellipsis; }

.acct-plan { padding: 14px; display: flex; flex-direction: column; gap: 10px; }
.acct-plan-head { display: flex; align-items: center; justify-content: space-between; }
.acct-plan-name { font-size: 0.95rem; font-weight: 700; color: #fff; }
.acct-plan-status { font-size: 0.8rem; color: #4ade80; font-weight: 600; }
.acct-plan-bar { height: 6px; border-radius: 3px; background: rgba(255, 255, 255, 0.08); overflow: hidden; }
.acct-plan-fill { height: 6px; border-radius: 3px; background: #60a5fa; }
.acct-plan-meta { font-size: 0.78rem; color: #8aa0aa; font-variant-numeric: tabular-nums; }

.acct-menu { overflow: hidden; display: flex; flex-direction: column; }
.acct-row {
  display: flex; align-items: center; gap: 12px; min-height: 52px; padding: 0 14px;
  background: transparent; border: none; border-top: 1px solid rgba(255, 255, 255, 0.06);
  color: #eef3f2; font: inherit; font-size: 0.95rem; text-align: left; cursor: pointer;
}
.acct-row:first-child { border-top: none; }
.acct-row:hover { background: rgba(255, 255, 255, 0.03); }
.acct-row.danger { color: #ff8a80; }
.acct-row-label { flex: 1 1 auto; min-width: 0; }

.acct-models { min-width: 0; }

.acct-note {
  display: flex; gap: 10px; padding: 12px 14px; border-radius: 14px;
  border: 1px dashed rgba(255, 255, 255, 0.14); color: #8aa0aa; font-size: 0.82rem; line-height: 1.45;
}

.confirm-modal {
  background: var(--duallo-card-gradient); border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 16px; box-shadow: 0 24px 60px rgba(0, 0, 0, 0.45); color: #fff; padding: 26px 22px 20px; text-align: center;
}
.confirm-icon { width: 52px; height: 52px; margin: 0 auto 12px; border-radius: 14px; display: flex; align-items: center; justify-content: center; background: rgba(239, 68, 68, 0.1); border: 1px solid rgba(239, 68, 68, 0.25); }
.confirm-title { font-size: 1.2rem; font-weight: 700; margin: 0 0 6px; }
.confirm-text { font-size: 0.92rem; color: #b0bec5; margin: 0 0 20px; line-height: 1.5; }
.confirm-actions { display: flex; gap: 10px; justify-content: center; }
.confirm-actions > * { flex: 1 1 0; max-width: 160px; }
.btn-ghost { padding: 10px 18px; border-radius: 10px; background: rgba(255, 255, 255, 0.04); border: 1px solid rgba(255, 255, 255, 0.12); color: #cfd8dc; font: inherit; font-weight: 600; cursor: pointer; }
.btn-danger { padding: 10px 18px; border-radius: 10px; background: rgba(239, 68, 68, 0.15); border: 1px solid rgba(239, 68, 68, 0.4); color: #f87171; font: inherit; font-weight: 700; cursor: pointer; }

/* ═══════════ Desktop (tabbed) ═══════════ */
.profile { width: 100%; min-height: 100%; display: flex; flex-direction: column; }
.profile-head { margin-bottom: 18px; }
.profile-title { font-size: clamp(1.5rem, calc(12px + 1vw), 1.9rem); font-weight: 800; color: #fff; margin: 0; }
.profile-sub { margin: 6px 0 0; font-size: 0.92rem; color: #8aa0aa; }

.tabbar { display: flex; gap: 4px; border-bottom: 1px solid rgba(255, 255, 255, 0.08); margin-bottom: 22px; overflow-x: auto; scrollbar-width: none; }
.tabbar::-webkit-scrollbar { display: none; }
.tab {
  display: inline-flex; align-items: center; gap: 6px; padding: 10px 4px; margin: 0 12px -1px 0;
  background: none; border: none; border-bottom: 2px solid transparent;
  color: #8aa0aa; font: inherit; font-size: 0.92rem; font-weight: 600; cursor: pointer; white-space: nowrap;
  transition: color 0.15s ease, border-color 0.15s ease;
}
.tab:hover:not(.active) { color: #cfd8dc; }
.tab.active { color: #fff; border-bottom-color: var(--duallo-accent); }

.tab-body { flex: 1 1 auto; min-width: 0; min-height: 0; }

.profile-grid { display: grid; grid-template-columns: minmax(0, 1fr) minmax(0, 1fr); gap: 18px; align-items: start; }
@media (max-width: 900px) { .profile-grid { grid-template-columns: 1fr; } }

.set-card {
  background: var(--duallo-card-gradient); border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 14px; box-shadow: 0 4px 14px rgba(0, 0, 0, 0.25); padding: clamp(16px, calc(8px + 0.6vw), 22px); min-width: 0;
}
.set-head { margin-bottom: 12px; }
.set-title { font-size: 1.02rem; font-weight: 700; color: #fff; margin: 0; }
.set-sub { margin: 4px 0 0; font-size: 0.85rem; color: #8aa0aa; }
.set-note { margin: 0; font-size: 0.9rem; color: #8aa0aa; }
</style>
